import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { staffService } from "../services/staffService";
import i18n from "@/i18n";
import type {
  InstitutionRole,
  CreateInstitutionRolePayload,
  UpdateInstitutionRolePayload,
  StaffMember,
  CreateStaffPayload,
  UpdateStaffPayload,
  StaffCredential,
  StaffListParams,
  MarkStaffAttendancePayload,
  StaffAttendanceRecord,
  StaffAttendanceRosterResponse,
  SalaryComponent,
  CreateSalaryComponentPayload,
  SalaryStructure,
  UpsertSalaryStructurePayload,
  StaffAssignment,
  CreateAssignmentPayload,
} from "../types";

interface StaffState {
  roles: InstitutionRole[];
  rolesLoading: boolean;
  rolesError: string | null;

  staff: StaffMember[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
  roleCounts: Record<string, number> | null;
  staffLoading: boolean;
  staffError: string | null;

  creatingStaff: boolean;
  createStaffError: string | null;

  lastIssuedCredential: StaffCredential | null;
  resettingPassword: boolean;
  resetPasswordError: string | null;

  attendanceRoster: StaffAttendanceRosterResponse | null;
  attendanceLoading: boolean;
  attendanceError: string | null;
  markingAttendance: boolean;

  // Detail page (single staff member)
  selectedStaff: StaffMember | null;
  selectedStaffLoading: boolean;
  updatingStaff: boolean;
  updateStaffError: string | null;

  salaryComponents: SalaryComponent[];
  salaryComponentsLoading: boolean;
  currentSalaryStructure: SalaryStructure | null;
  salaryStructureHistory: SalaryStructure[];
  salaryLoading: boolean;
  salaryError: string | null;

  assignments: StaffAssignment[];
  assignmentHistory: StaffAssignment[];
  assignmentsLoading: boolean;
  assignmentError: string | null;
}

const initialState: StaffState = {
  roles: [],
  rolesLoading: false,
  rolesError: null,

  staff: [],
  pagination: null,
  roleCounts: null,
  staffLoading: false,
  staffError: null,

  creatingStaff: false,
  createStaffError: null,

  lastIssuedCredential: null,
  resettingPassword: false,
  resetPasswordError: null,

  attendanceRoster: null,
  attendanceLoading: false,
  attendanceError: null,
  markingAttendance: false,

  selectedStaff: null,
  selectedStaffLoading: false,
  updatingStaff: false,
  updateStaffError: null,

  salaryComponents: [],
  salaryComponentsLoading: false,
  currentSalaryStructure: null,
  salaryStructureHistory: [],
  salaryLoading: false,
  salaryError: null,

  assignments: [],
  assignmentHistory: [],
  assignmentsLoading: false,
  assignmentError: null,
};

// ---------- Thunks ----------

export const fetchInstitutionRoles = createAsyncThunk(
  "staff/fetchInstitutionRoles",
  async (institutionId: number) => {
    try {
      return await staffService.getInstitutionRoles(institutionId);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToLoadRoles"));
    }
  }
);

export const createInstitutionRole = createAsyncThunk(
  "staff/createInstitutionRole",
  async ({
    institutionId,
    payload,
  }: {
    institutionId: number;
    payload: CreateInstitutionRolePayload;
  }) => {
    try {
      return await staffService.createInstitutionRole(institutionId, payload);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToCreateRole"));
    }
  }
);

// Only reaches this institution's own custom (DYNAMIC) roles — the backend
// rejects canonical fixed roles (isCanonical: true) with 404.
export const updateInstitutionRole = createAsyncThunk(
  "staff/updateInstitutionRole",
  async ({
    institutionId,
    roleId,
    payload,
  }: {
    institutionId: number;
    roleId: number;
    payload: UpdateInstitutionRolePayload;
  }) => {
    try {
      return await staffService.updateInstitutionRole(institutionId, roleId, payload);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToUpdateRole"));
    }
  }
);

// Backend returns 409 (surfaced via err.response.data.error) if any
// staff/login still holds this role.
export const deleteInstitutionRole = createAsyncThunk(
  "staff/deleteInstitutionRole",
  async ({ institutionId, roleId }: { institutionId: number; roleId: number }) => {
    try {
      await staffService.deleteInstitutionRole(institutionId, roleId);
      return roleId;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToDeleteRole"));
    }
  }
);

export const fetchStaff = createAsyncThunk(
  "staff/fetchStaff",
  async ({
    institutionId,
    params,
  }: {
    institutionId: number;
    params?: StaffListParams;
  }) => {
    try {
      return await staffService.getStaff(institutionId, params);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToLoadStaff"));
    }
  }
);

export const createStaff = createAsyncThunk(
  "staff/createStaff",
  async ({
    institutionId,
    payload,
  }: {
    institutionId: number;
    payload: CreateStaffPayload;
  }) => {
    try {
      return await staffService.createStaff(institutionId, payload);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToCreateStaff"));
    }
  }
);

/** Admin-driven reset — POST /users/:userId/reset-password. userId is institution_staff.userId, not staff.id. */
export const resetStaffPassword = createAsyncThunk(
  "staff/resetStaffPassword",
  async ({ userId }: { userId: number }) => {
    try {
      const { email, tempPassword } = await staffService.resetStaffPassword(userId);
      return { email, tempPassword };
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToResetPassword"));
    }
  }
);

export const markStaffAttendance = createAsyncThunk(
  "staff/markStaffAttendance",
  async ({
    institutionId,
    staffId,
    payload,
  }: {
    institutionId: number;
    staffId: number;
    payload: MarkStaffAttendancePayload;
  }) => {
    try {
      return await staffService.markStaffAttendance(institutionId, staffId, payload);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToMarkAttendance"));
    }
  }
);

export const fetchStaffAttendanceRoster = createAsyncThunk(
  "staff/fetchStaffAttendanceRoster",
  async ({ institutionId, date }: { institutionId: number; date: string }) => {
    try {
      return await staffService.getStaffAttendanceRoster(institutionId, date);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToLoadAttendance"));
    }
  }
);

export const fetchStaffById = createAsyncThunk(
  "staff/fetchStaffById",
  async ({ institutionId, staffId }: { institutionId: number; staffId: number }) => {
    try {
      return await staffService.getStaffById(institutionId, staffId);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToLoadStaffMember"));
    }
  }
);

export const updateStaff = createAsyncThunk(
  "staff/updateStaff",
  async ({
    institutionId,
    staffId,
    payload,
  }: {
    institutionId: number;
    staffId: number;
    payload: UpdateStaffPayload;
  }) => {
    try {
      return await staffService.updateStaff(institutionId, staffId, payload);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToUpdateStaffMember"));
    }
  }
);

export const fetchSalaryComponents = createAsyncThunk(
  "staff/fetchSalaryComponents",
  async (institutionId: number) => {
    try {
      return await staffService.getSalaryComponents(institutionId);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToLoadSalaryComponents"));
    }
  }
);

export const createSalaryComponent = createAsyncThunk(
  "staff/createSalaryComponent",
  async ({ institutionId, payload }: { institutionId: number; payload: CreateSalaryComponentPayload }) => {
    try {
      return await staffService.createSalaryComponent(institutionId, payload);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToCreateSalaryComponent"));
    }
  }
);

export const fetchSalaryStructure = createAsyncThunk(
  "staff/fetchSalaryStructure",
  async ({
    institutionId,
    staffId,
    academicYearId,
  }: {
    institutionId: number;
    staffId: number;
    academicYearId: number;
  }) => {
    try {
      return await staffService.getSalaryStructure(institutionId, staffId, academicYearId);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToLoadSalaryStructure"));
    }
  }
);

export const upsertSalaryStructure = createAsyncThunk(
  "staff/upsertSalaryStructure",
  async ({
    institutionId,
    staffId,
    payload,
  }: {
    institutionId: number;
    staffId: number;
    payload: UpsertSalaryStructurePayload;
  }) => {
    try {
      return await staffService.upsertSalaryStructure(institutionId, staffId, payload);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToSaveSalaryStructure"));
    }
  }
);

export const fetchSalaryStructureHistory = createAsyncThunk(
  "staff/fetchSalaryStructureHistory",
  async ({ institutionId, staffId }: { institutionId: number; staffId: number }) => {
    try {
      return await staffService.getSalaryStructureHistory(institutionId, staffId);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToLoadSalaryHistory"));
    }
  }
);

export const fetchAssignments = createAsyncThunk(
  "staff/fetchAssignments",
  async ({
    institutionId,
    staffId,
    academicYearId,
  }: {
    institutionId: number;
    staffId: number;
    academicYearId: number;
  }) => {
    try {
      return await staffService.getAssignments(institutionId, staffId, academicYearId);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToLoadAssignments"));
    }
  }
);

export const createAssignment = createAsyncThunk(
  "staff/createAssignment",
  async (
    {
      institutionId,
      staffId,
      payload,
    }: { institutionId: number; staffId: number; payload: CreateAssignmentPayload },
    { dispatch }
  ) => {
    try {
      const assignment = await staffService.createAssignment(institutionId, staffId, payload);
      await dispatch(fetchAssignments({ institutionId, staffId, academicYearId: payload.academicYearId }));
      return assignment;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToCreateAssignment"));
    }
  }
);

export const removeAssignment = createAsyncThunk(
  "staff/removeAssignment",
  async (
    {
      institutionId,
      staffId,
      assignmentId,
      academicYearId,
    }: { institutionId: number; staffId: number; assignmentId: number; academicYearId: number },
    { dispatch }
  ) => {
    try {
      await staffService.removeAssignment(institutionId, staffId, assignmentId);
      await dispatch(fetchAssignments({ institutionId, staffId, academicYearId }));
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToRemoveAssignment"));
    }
  }
);

export const fetchAssignmentHistory = createAsyncThunk(
  "staff/fetchAssignmentHistory",
  async ({ institutionId, staffId }: { institutionId: number; staffId: number }) => {
    try {
      return await staffService.getAssignmentHistory(institutionId, staffId);
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || i18n.t("staff:errors.failedToLoadAssignmentHistory"));
    }
  }
);

// ---------- Slice ----------

const staffSlice = createSlice({
  name: "staff",
  initialState,
  reducers: {
    clearLastIssuedCredential: (state) => {
      state.lastIssuedCredential = null;
    },
    clearCreateStaffError: (state) => {
      state.createStaffError = null;
    },
    clearSelectedStaff: (state) => {
      state.selectedStaff = null;
      state.currentSalaryStructure = null;
      state.salaryStructureHistory = [];
      state.assignments = [];
      state.assignmentHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // roles: fetch
      .addCase(fetchInstitutionRoles.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(
        fetchInstitutionRoles.fulfilled,
        (state, action: PayloadAction<InstitutionRole[]>) => {
          state.rolesLoading = false;
          state.roles = action.payload;
        }
      )
      .addCase(fetchInstitutionRoles.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.error.message ?? i18n.t("staff:errors.failedToLoadRoles");
      })

      // roles: create (inline quick-add)
      .addCase(createInstitutionRole.fulfilled, (state, action: PayloadAction<InstitutionRole>) => {
        state.roles.push(action.payload);
      })

      // roles: update
      .addCase(updateInstitutionRole.fulfilled, (state, action: PayloadAction<InstitutionRole>) => {
        const index = state.roles.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) state.roles[index] = action.payload;
      })

      // roles: delete
      .addCase(deleteInstitutionRole.fulfilled, (state, action: PayloadAction<number>) => {
        state.roles = state.roles.filter((r) => r.id !== action.payload);
      })

      // staff: fetch list
      .addCase(fetchStaff.pending, (state) => {
        state.staffLoading = true;
        state.staffError = null;
      })
      .addCase(fetchStaff.fulfilled, (state, action) => {
        state.staffLoading = false;
        state.staff = action.payload.staff;
        state.pagination = action.payload.pagination;
        state.roleCounts = action.payload.roleCounts ?? null;
      })
      .addCase(fetchStaff.rejected, (state, action) => {
        state.staffLoading = false;
        state.staffError = action.error.message ?? i18n.t("staff:errors.failedToLoadStaff");
      })

      // staff: create
      .addCase(createStaff.pending, (state) => {
        state.creatingStaff = true;
        state.createStaffError = null;
      })
      .addCase(createStaff.fulfilled, (state, action) => {
        state.creatingStaff = false;
        state.staff.unshift(action.payload.staff);
        state.lastIssuedCredential = action.payload.credential ?? null;
      })
      .addCase(createStaff.rejected, (state, action) => {
        state.creatingStaff = false;
        state.createStaffError = action.error.message ?? i18n.t("staff:errors.failedToCreateStaff");
      })

      // staff: reset password (admin-driven)
      .addCase(resetStaffPassword.pending, (state) => {
        state.resettingPassword = true;
        state.resetPasswordError = null;
      })
      .addCase(resetStaffPassword.fulfilled, (state, action) => {
        state.resettingPassword = false;
        state.lastIssuedCredential = action.payload;
      })
      .addCase(resetStaffPassword.rejected, (state, action) => {
        state.resettingPassword = false;
        state.resetPasswordError = action.error.message ?? i18n.t("staff:errors.failedToResetPassword");
      })

      // attendance: mark
      .addCase(markStaffAttendance.pending, (state) => {
        state.markingAttendance = true;
      })
      .addCase(
        markStaffAttendance.fulfilled,
        (state, action: PayloadAction<StaffAttendanceRecord>) => {
          state.markingAttendance = false;
          if (state.attendanceRoster) {
            const idx = state.attendanceRoster.attendance.findIndex(
              (a) => a.staffId === action.payload.staffId
            );
            if (idx >= 0) {
              state.attendanceRoster.attendance[idx] = action.payload;
            } else {
              state.attendanceRoster.attendance.push(action.payload);
            }
          }
        }
      )
      .addCase(markStaffAttendance.rejected, (state) => {
        state.markingAttendance = false;
      })

      // attendance: roster fetch
      .addCase(fetchStaffAttendanceRoster.pending, (state) => {
        state.attendanceLoading = true;
        state.attendanceError = null;
      })
      .addCase(fetchStaffAttendanceRoster.fulfilled, (state, action) => {
        state.attendanceLoading = false;
        state.attendanceRoster = action.payload;
      })
      .addCase(fetchStaffAttendanceRoster.rejected, (state, action) => {
        state.attendanceLoading = false;
        state.attendanceError = action.error.message ?? i18n.t("staff:errors.failedToLoadAttendance");
      })

      // detail: fetch one staff member
      .addCase(fetchStaffById.pending, (state) => {
        state.selectedStaffLoading = true;
      })
      .addCase(fetchStaffById.fulfilled, (state, action) => {
        state.selectedStaffLoading = false;
        state.selectedStaff = action.payload;
      })
      .addCase(fetchStaffById.rejected, (state) => {
        state.selectedStaffLoading = false;
      })

      // detail: update profile
      .addCase(updateStaff.pending, (state) => {
        state.updatingStaff = true;
        state.updateStaffError = null;
      })
      .addCase(updateStaff.fulfilled, (state, action) => {
        state.updatingStaff = false;
        state.selectedStaff = action.payload;
        const idx = state.staff.findIndex((s) => s.id === action.payload.id);
        if (idx >= 0) state.staff[idx] = action.payload;
      })
      .addCase(updateStaff.rejected, (state, action) => {
        state.updatingStaff = false;
        state.updateStaffError = action.error.message ?? i18n.t("staff:errors.failedToUpdateStaffMember");
      })

      // salary components
      .addCase(fetchSalaryComponents.pending, (state) => {
        state.salaryComponentsLoading = true;
      })
      .addCase(fetchSalaryComponents.fulfilled, (state, action) => {
        state.salaryComponentsLoading = false;
        state.salaryComponents = action.payload;
      })
      .addCase(fetchSalaryComponents.rejected, (state) => {
        state.salaryComponentsLoading = false;
      })
      .addCase(createSalaryComponent.fulfilled, (state, action) => {
        state.salaryComponents.push(action.payload);
      })
      .addCase(createSalaryComponent.rejected, (state, action) => {
        state.salaryError = action.error.message ?? i18n.t("staff:errors.failedToCreateSalaryComponent");
      })

      // salary structure (current year)
      .addCase(fetchSalaryStructure.pending, (state) => {
        state.salaryLoading = true;
        state.salaryError = null;
      })
      .addCase(fetchSalaryStructure.fulfilled, (state, action) => {
        state.salaryLoading = false;
        state.currentSalaryStructure = action.payload;
      })
      .addCase(fetchSalaryStructure.rejected, (state, action) => {
        state.salaryLoading = false;
        state.salaryError = action.error.message ?? i18n.t("staff:errors.failedToLoadSalaryStructure");
      })
      .addCase(upsertSalaryStructure.pending, (state) => {
        state.salaryLoading = true;
        state.salaryError = null;
      })
      .addCase(upsertSalaryStructure.fulfilled, (state, action) => {
        state.salaryLoading = false;
        state.currentSalaryStructure = action.payload;
      })
      .addCase(upsertSalaryStructure.rejected, (state, action) => {
        state.salaryLoading = false;
        state.salaryError = action.error.message ?? i18n.t("staff:errors.failedToSaveSalaryStructure");
      })
      .addCase(fetchSalaryStructureHistory.fulfilled, (state, action) => {
        state.salaryStructureHistory = action.payload;
      })

      // assignments
      .addCase(fetchAssignments.pending, (state) => {
        state.assignmentsLoading = true;
        state.assignmentError = null;
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.assignmentsLoading = false;
        state.assignments = action.payload;
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.assignmentsLoading = false;
        state.assignmentError = action.error.message ?? i18n.t("staff:errors.failedToLoadAssignments");
      })
      .addCase(createAssignment.rejected, (state, action) => {
        state.assignmentError = action.error.message ?? i18n.t("staff:errors.failedToCreateAssignment");
      })
      .addCase(removeAssignment.rejected, (state, action) => {
        state.assignmentError = action.error.message ?? i18n.t("staff:errors.failedToRemoveAssignment");
      })
      .addCase(fetchAssignmentHistory.fulfilled, (state, action) => {
        state.assignmentHistory = action.payload;
      });
  },
});

export const { clearLastIssuedCredential, clearCreateStaffError, clearSelectedStaff } = staffSlice.actions;
export default staffSlice.reducer;